import { Box, Checkbox, Link, Stack } from '@montugroup/design-system';
import Info from '@mui/icons-material/Info';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { styled } from '@mui/material';

import {
  usePharmacistApprovalDocumentsModal,
  usePharmacistUnableToDispenseModal,
} from '@/context/orders/PharmacistOrdersModals';
import type { DELIVERY_OPTION, DELIVERY_OPTIONS, PrescriptionDetails } from '@montugroup/circuit-shared';
import { useOrderIsSelected, useToggleOrder } from '@montugroup/circuit-shared/context/table/OrderSelectionContext';

const UnorderedList = styled('ul')`
  list-style: none;
  margin: 0;
  padding-inline-start: 0;
`;

const ApprovalsIcon = styled(PictureAsPdfIcon)`
  cursor: pointer;
`;

const ShipmentStatus = styled(Box)`
  margin-left: 0.25rem;
`;

const InfoIcon = styled(Info)`
  cursor: pointer;
`;

const SecondaryText = styled('span')(({ theme }) => ({
  color: theme.palette.grey['600'],
  marginLeft: theme.spacing(2),
}));

export function ApprovalsTableCell({ patientCode, orderId }: { patientCode: string; orderId: number }) {
  const { showApprovalDocumentsModal } = usePharmacistApprovalDocumentsModal();
  return <ApprovalsIcon aria-label="show approvals" onClick={() => showApprovalDocumentsModal(patientCode, orderId)} />;
}

export type PrescriptionLike = {
  path: string;
  name?: string;
  code?: string;
  prescriber?: {
    id: number;
    firstName: string;
    lastName: string;
  };
};

export function PrescriptionsTableCell<T extends PrescriptionLike>({
  prescriptions,
  fallbackGPName, // TODO: remove once /pharmacist/orders updated to include prescription.prescriber
}: {
  prescriptions: T[];
  fallbackGPName?: string;
}) {
  return (
    <UnorderedList>
      {prescriptions.map((prescription) => {
        const name = prescription.name ?? prescription.code;
        const prescriberName = prescription.prescriber
          ? `${prescription.prescriber.firstName} ${prescription.prescriber.lastName}`
          : fallbackGPName ?? '';
        return (
          <li key={name}>
            <Stack direction="row" gap={1} sx={{ minWidth: '25rem' }}>
              <Link href={prescription.path} target="_blank">
                {name}
              </Link>
              {name && prescriberName && (
                <Box>
                  -<SecondaryText>Dr. {prescriberName}</SecondaryText>
                </Box>
              )}
            </Stack>
          </li>
        );
      })}
    </UnorderedList>
  );
}

export function DoctorNoteTableCell({ prescriptions }: { prescriptions: PrescriptionDetails[] }) {
  return (
    <UnorderedList>
      {prescriptions.map(({ dispense_note, name }) => (
        <li key={name}>{dispense_note}</li>
      ))}
    </UnorderedList>
  );
}

export function ProductNameTableCell({ productNames }: { productNames: string[] }) {
  return (
    <UnorderedList>
      {productNames.map((name) => (
        <li key={name}>{name}</li>
      ))}
    </UnorderedList>
  );
}

export function ProductNameWithQuantityTableCell({
  productNamesAndQuantities,
}: {
  productNamesAndQuantities: Array<{ id: string; name: string; quantity: number; repeats: number }>;
}) {
  return (
    <Box>
      <UnorderedList>
        {productNamesAndQuantities.map(({ id, name, quantity, repeats }) => (
          <li key={id}>
            <Stack direction="row" gap={4} sx={{ minWidth: '25rem' }}>
              <Box sx={{ width: '1rem' }}>
                <b>{quantity}x</b>
              </Box>
              <Box>
                {name}
                <SecondaryText>({repeats} repeats)</SecondaryText>
              </Box>
            </Stack>
          </li>
        ))}
      </UnorderedList>
    </Box>
  );
}

export function ArrayValueTableCell<T extends number | string>({ values }: { values: T[] }) {
  // todo look at adding unique identifiers for these
  return (
    <UnorderedList>
      {values.map((value, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${value}${index}`}>{value}</li>
      ))}
    </UnorderedList>
  );
}

export function ShipmentStatusTableCell({
  orderId,
  status,
  deliveryOption,
}: {
  orderId: number;
  status: string;
  deliveryOption?: Exclude<(typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'], 'ThreeHour'>;
}) {
  const { showUnableToDispenseModal } = usePharmacistUnableToDispenseModal();
  switch (status) {
    case 'Dispensed':
      if (deliveryOption === 'ClickAndCollect') {
        return 'Ready for collection';
      }
      return 'Dispensed';
    case 'Pharmacy out of Stock':
      return 'Out of stock';
    case 'Pharmacy Partially out of Stock':
      return 'Partially out of stock';
    case 'Unable to Dispense':
      return (
        <Stack direction="row" alignItems="center">
          <InfoIcon onClick={() => showUnableToDispenseModal(orderId)} />
          <ShipmentStatus>Unable to dispense</ShipmentStatus>
        </Stack>
      );
    case 'Awaiting Shipment':
      return 'Pending Stock';
    default:
      return status;
  }
}

export function OrderSelectTableCell({ orderId }: { orderId: number }) {
  const isSelected = useOrderIsSelected(orderId);
  const toggleOrder = useToggleOrder();
  return <Checkbox checked={isSelected} onChange={() => toggleOrder(orderId)} />;
}

export function DoctorTableCell({ gps }: { gps: string[] }) {
  return (
    <UnorderedList>
      {gps.map((gp) => (
        <li key={gp}>{gp}</li>
      ))}
    </UnorderedList>
  );
}
